/* ==========================================================================
   ENA — PDP behaviour
   Countdown ticking, pack selection -> quantity, and purchase-mode selection.
   All of it is defensive: if a piece of markup is absent the rest still runs,
   and re-running after a section re-render is safe.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     Countdown — ticks toward a real end datetime and removes itself when
     the offer is over. Never restarts, so it can't become evergreen.
     --------------------------------------------------------------------- */

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function startCountdown(el) {
    if (el.dataset.enaCountdownReady === '1') return;
    el.dataset.enaCountdownReady = '1';

    var clock = el.querySelector('[data-ena-countdown-clock]');
    var endsAt = Date.parse(el.dataset.endsAt);
    if (!clock || isNaN(endsAt)) {
      el.remove();
      return;
    }

    function tick() {
      var left = endsAt - Date.now();
      if (left <= 0) {
        clearInterval(timer);
        el.remove();
        return;
      }
      var days = Math.floor(left / 86400000);
      var hours = Math.floor((left % 86400000) / 3600000);
      var mins = Math.floor((left % 3600000) / 60000);
      var secs = Math.floor((left % 60000) / 1000);
      // Beyond a day, a raw hour count reads as noise (e.g. 2952:00:00).
      var stamp = pad(hours) + ':' + pad(mins) + ':' + pad(secs);
      clock.textContent = days > 0 ? days + 'd ' + stamp : stamp;
    }

    tick();
    var timer = setInterval(tick, 1000);
  }

  /* ---------------------------------------------------------------------
     Pack builder — QA ronda 6 (15/09 · item 1). The tier cards and the -/+
     stepper are one control seen twice: picking a card writes its quantity
     into the form, and stepping the quantity selects the tier that covers it
     (1 → x1, 2 → x2, 3 or more → x3+). The add-to-cart label carries the
     price of the selected quantity with the tier's discount applied, so it
     no longer sits on the unit price whatever the quantity is.

     Prices are computed here from data attributes the snippet emits
     (data-ena-unit on the wrapper, data-ena-pack-qty / -pct on each input)
     and formatted with the shop's money format, so they match Liquid's
     `| money` output.
     --------------------------------------------------------------------- */

  function formatMoney(cents) {
    var format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '${{amount}}';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    var key = match ? match[1] : 'amount';
    var value = Math.round(cents) / 100;
    function group(numStr, thousands, decimal, precision) {
      var fixed = value.toFixed(precision);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? parts[0] + decimal + parts[1] : parts[0];
    }
    var out;
    switch (key) {
      case 'amount_no_decimals': out = group(value, ',', '.', 0); break;
      case 'amount_with_comma_separator': out = group(value, '.', ',', 2); break;
      case 'amount_no_decimals_with_comma_separator': out = group(value, '.', ',', 0); break;
      case 'amount_with_apostrophe_separator': out = group(value, "'", '.', 2); break;
      case 'amount_no_decimals_with_space_separator': out = group(value, ' ', ',', 0); break;
      case 'amount_with_space_separator': out = group(value, ' ', ',', 2); break;
      case 'amount_with_period_and_space_separator': out = group(value, ' ', '.', 2); break;
      default: out = group(value, ',', '.', 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]*>/g, '');
  }

  function findQuantityInput(scope) {
    var root = scope.closest('.shopify-section') || document;
    return (
      root.querySelector('quantity-selector input[name="quantity"]') ||
      root.querySelector('input[name="quantity"]')
    );
  }

  function packTiers(wrapper) {
    return Array.prototype.map.call(wrapper.querySelectorAll('input[data-ena-pack-qty]'), function (input) {
      return {
        input: input,
        qty: parseInt(input.dataset.enaPackQty, 10) || 1,
        pct: parseFloat(input.dataset.enaPackPct) || 0
      };
    }).sort(function (a, b) { return a.qty - b.qty; });
  }

  /* The tier that covers a quantity: the largest tier whose quantity is not
     above it, so 4 units still ride on the "3+" tier. */
  function tierFor(tiers, qty) {
    /* R9 · 26/10: below the smallest tier there is no tier (it used to fall
       back to tiers[0], so a list starting at x2 marked x2 for 1 unit and the
       CTA showed one unit at the x2 discount). */
    var hit = null;
    tiers.forEach(function (t) { if (t.qty <= qty) hit = t; });
    return hit;
  }

  function packTotal(wrapper, qty) {
    var unit = parseInt(wrapper.dataset.enaUnit, 10) || 0;
    var tier = tierFor(packTiers(wrapper), qty);
    var pct = tier ? tier.pct : 0;
    return Math.round(unit * qty * (100 - pct) / 100);
  }

  function markSelected(wrapper, tier) {
    wrapper.querySelectorAll('.ena-pack').forEach(function (card) {
      var input = card.querySelector('input[data-ena-pack-qty]');
      var on = !!(tier && input === tier.input);
      card.classList.toggle('is-selected', on);
      if (input && input.checked !== on) input.checked = on;
    });
  }

  /* The CTA's price span is the one-time price the purchase-mode code
     restores when the shopper leaves "Suscribirme" (data-ena-orig), so the
     pack total is written there as well as on screen. */
  /* R8 · 08/10-2: the "$533 / servicio" chip follows the pack — total of the
     selected tier over its servings (servings × qty), so "Ahorrá 20%" on x2
     reads as a cheaper serving, which is the point of the pill. Liquid paints
     the per-variant value on render; this only repaints on pack changes. */
  function paintCostPerServing(section, total, qty) {
    section.querySelectorAll('[data-ena-cost-chip]').forEach(function (chip) {
      var servings = parseInt(chip.dataset.enaCostServings, 10) || 0;
      var value = chip.querySelector('[data-ena-cost-value]');
      if (!servings || !value || !qty) return;
      value.textContent = formatMoney(total / (servings * qty));
    });
  }

  function paintPackPrice(wrapper, qty) {
    var section = wrapper.closest('.shopify-section') || document;
    var total = packTotal(wrapper, qty);
    var html = '&nbsp;· ' + formatMoney(total);
    paintCostPerServing(section, total, qty);
    section.querySelectorAll('buy-buttons button[type="submit"] .buy-buttons__price').forEach(function (el) {
      el.dataset.enaOrig = html;
      var btn = el.closest('button');
      if (!btn || !btn.classList.contains('is-subscribe')) el.innerHTML = html;
    });
    if (typeof refreshStickyAtc === 'function') refreshStickyAtc();
  }

  /* R9 · 26/10: nothing looks preselected. A card is marked only when the
     shopper has touched the cards or the stepper, or when the form already
     holds more than one unit (Impact keeps the quantity across variant
     changes) — so the marked card always matches what goes to the cart. */
  function syncPacksFromQuantity(wrapper) {
    var qtyInput = findQuantityInput(wrapper);
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var section = wrapper.closest('.shopify-section') || document.body;
    var touched = section.dataset.enaPackTouched === '1';
    markSelected(wrapper, (touched || qty > 1) ? tierFor(packTiers(wrapper), qty) : null);
    paintPackPrice(wrapper, qty);
  }

  function initPacks(wrapper) {
    if (wrapper.dataset.enaPacksReady === '1') return;
    wrapper.dataset.enaPacksReady = '1';

    wrapper.addEventListener('change', function (event) {
      var input = event.target.closest('input[data-ena-pack-qty]');
      if (!input) return;
      (wrapper.closest('.shopify-section') || document.body).dataset.enaPackTouched = '1';
      var tiers = packTiers(wrapper);
      var tier = tiers.filter(function (t) { return t.input === input; })[0];
      markSelected(wrapper, tier);

      var qty = parseInt(input.dataset.enaPackQty, 10) || 1;
      var qtyInput = findQuantityInput(wrapper);
      if (qtyInput) {
        qtyInput.value = qty;
        /* Impact's stepper listens for change; our own listener below reads
           the same event and repaints, so one dispatch covers both. */
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        paintPackPrice(wrapper, qty);
      }
    });

    /* Stepper → cards. Bound on the section, once, and resolved against the
       wrapper that is in the DOM at event time, because Impact re-renders the
       pack block on every variant change. */
    var section = wrapper.closest('.shopify-section') || document.body;
    if (section.dataset.enaPackQtyBound !== '1') {
      section.dataset.enaPackQtyBound = '1';
      ['change', 'input'].forEach(function (type) {
        section.addEventListener(type, function (event) {
          if (!event.target.matches || !event.target.matches('input[name="quantity"]')) return;
          section.dataset.enaPackTouched = '1';
          var live = section.querySelector('[data-ena-packs]');
          if (live) syncPacksFromQuantity(live);
        });
      });
    }

    /* First paint: honour whatever quantity the form already holds (Impact
       keeps it across variant changes) and put the total on the CTA. */
    syncPacksFromQuantity(wrapper);
  }

  /* ---------------------------------------------------------------------
     Purchase mode — moves the is-selected class so the chosen option
     expands. The selling_plan value posts with the form on its own.
     --------------------------------------------------------------------- */

  /* QA ronda 4 · items 3/4. The chosen mode has to show up where the decision
     is taken: the main CTA and the sticky bar switch their label to
     "Suscribirme" and show the one-time price struck next to the subscription
     price. Prices come from the mode block (data-ena-price / data-ena-sub-price),
     which Impact re-renders on every variant change, so they always describe
     the selected variant. Originals are stashed in data attributes so
     returning to "Compra única" restores them exactly. */

  /* Impact wraps the button label in a <div> next to the loader, so the label
     is the first non-empty text node anywhere inside the button that is not
     part of the price span or the loader. */
  function firstTextNode(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    var n;
    while ((n = walker.nextNode())) {
      if (!n.nodeValue.trim()) continue;
      if (n.parentElement.closest('.buy-buttons__price, .button__loader, .sr-only')) continue;
      return n;
    }
    return null;
  }

  function lastTextNode(el) {
    for (var i = el.childNodes.length - 1; i >= 0; i--) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) return n;
    }
    return null;
  }

  function setCtaButton(btn, sub, price, subPrice) {
    var label = firstTextNode(btn);
    if (!label) return;
    if (btn.dataset.enaLabel == null) btn.dataset.enaLabel = label.nodeValue;
    var priceEl = btn.querySelector('.buy-buttons__price');
    if (priceEl && priceEl.dataset.enaOrig == null) priceEl.dataset.enaOrig = priceEl.innerHTML;

    if (sub) {
      label.nodeValue = 'Suscribirme';
      if (priceEl) {
        priceEl.innerHTML =
          ' · <s class="buy-buttons__was">' + price + '</s> ' + subPrice;
      }
    } else {
      label.nodeValue = btn.dataset.enaLabel;
      if (priceEl) priceEl.innerHTML = priceEl.dataset.enaOrig;
    }
    btn.classList.toggle('is-subscribe', sub);
  }

  /* Impact's price-list: <sale-price> and <compare-at-price>, each with an
     sr-only prefix before the amount, so only the last text node is touched. */
  function setPriceList(list, sub, price, subPrice) {
    var sale = list.querySelector('sale-price');
    var compare = list.querySelector('compare-at-price');
    if (!sale) return;
    var saleText = lastTextNode(sale);
    if (!saleText) return;
    if (sale.dataset.enaOrig == null) sale.dataset.enaOrig = saleText.nodeValue;
    var compText = compare ? lastTextNode(compare) : null;
    if (compare && compText && compare.dataset.enaOrig == null) {
      compare.dataset.enaOrig = compText.nodeValue;
      compare.dataset.enaHidden = compare.hidden ? '1' : '0';
    }

    if (sub) {
      saleText.nodeValue = subPrice;
      if (compText) {
        compText.nodeValue = price;
        compare.hidden = false;
      }
    } else {
      saleText.nodeValue = sale.dataset.enaOrig;
      if (compText) {
        compText.nodeValue = compare.dataset.enaOrig;
        compare.hidden = compare.dataset.enaHidden === '1';
      }
    }
  }

  function applyModeToCtas(wrapper) {
    var checked = wrapper.querySelector('input[name="ena-purchase-mode"]:checked');
    var subLabel = wrapper.querySelector('[data-ena-sub-price]');
    var subCard = wrapper.querySelector('[data-reval-sub]');
    var cardShown = !subCard || !subCard.hidden;
    var sub = !!(checked && checked.value === 'subscribe' && subLabel && cardShown);
    var price = wrapper.dataset.enaPrice || '';
    var subPrice = subLabel ? subLabel.dataset.enaSubPrice : '';
    var section = wrapper.closest('.shopify-section') || document;

    section.querySelectorAll('buy-buttons button[type="submit"]').forEach(function (btn) {
      setCtaButton(btn, sub, price, subPrice);
    });
    section.querySelectorAll('product-quick-add price-list').forEach(function (list) {
      setPriceList(list, sub, price, subPrice);
    });
    refreshStickyAtc();
  }

  function initModes(wrapper) {
    if (wrapper.dataset.enaModeReady === '1') return;
    wrapper.dataset.enaModeReady = '1';

    wrapper.addEventListener('change', function (event) {
      var input = event.target.closest('input[name="ena-purchase-mode"]');
      if (!input) return;

      wrapper.querySelectorAll('.ena-mode__option').forEach(function (option) {
        option.classList.toggle('is-selected', option.contains(input));
      });

      /* QA-07: the subscription card is a wrapper around the label now, so it
         carries the selected state too - the gifts inside it are shown only
         while subscription is the chosen mode. */
      wrapper.querySelectorAll('.ena-mode__sub').forEach(function (card) {
        card.classList.toggle('is-selected', card.contains(input));
      });

      /* ENA-15: remember the choice so Impact's re-render on a flavour change
         does not silently flip the shopper back to the default. */
      if (!reval.silent) reval.mode = input.value;
      revalSyncQtyNote(wrapper);

      // A one-time purchase must not carry a selling plan.
      var select = wrapper.querySelector('select[name="selling_plan"]');
      if (select) select.disabled = input.value !== 'subscribe';

      applyModeToCtas(wrapper);
    });
  }


  /* ---------------------------------------------------------------------
     Reval (ENA-15). ENA sells subscriptions through Reval (Mercado Pago), not
     Shopify selling plans, so the "Suscribirme y ahorrar" card is only real
     when Reval can sell THIS product. The Liquid (ena-purchase-mode) says what
     kind of product this is; here we ask Reval, fill the card, and in subscribe
     mode send the CTA to Reval's checkout instead of the cart form.

       · data-reval-kind="single"  -> GET /apps/subscriptions/plans?variant_id=
                                      (the public feed Reval's own buy-box reads)
       · data-reval-kind="combo"   -> the dynamic box's builder page
                                      (/apps/subscriptions/caja/<handle>, `var S`),
                                      priced like reval-combo-subscribe.js
       · data-reval-kind="none"    -> nothing to sell, card stays hidden
     Shopify selling plans (data-ena-has-plans="true") are left alone.
     --------------------------------------------------------------------- */

  var reval = {
    mode: null,          // the shopper's explicit choice, kept across variant changes
    recurrence: null,    // chosen cadence, idem
    feed: new Map(),     // variant id -> Promise<feed json>
    builder: new Map(),  // box handle -> Promise<S>
    navigating: false
  };

  function revalNumericId(v) {
    var m = String(v || '').match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }

  function revalFetchFeed(variantId) {
    if (!reval.feed.has(variantId)) {
      var p = fetch('/apps/subscriptions/plans?variant_id=' + encodeURIComponent(variantId), {
        credentials: 'same-origin', headers: { Accept: 'application/json' }
      }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
      reval.feed.set(variantId, p);
      p.catch(function () { reval.feed.delete(variantId); });
    }
    return reval.feed.get(variantId);
  }

  function revalFetchBuilder(handle) {
    if (!reval.builder.has(handle)) {
      var pre = window.__rvcBox && window.__rvcBox[handle];
      var html = pre || fetch('/apps/subscriptions/caja/' + encodeURIComponent(handle), {
        credentials: 'same-origin', headers: { Accept: 'text/html' }
      }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); });
      var p = html.then(function (text) {
        var m = text.match(/var S = (\{[\s\S]*?\});\s*\n/);
        if (!m) throw new Error('builder state not found');
        return JSON.parse(m[1]);
      });
      reval.builder.set(handle, p);
      p.catch(function () { reval.builder.delete(handle); });
    }
    return reval.builder.get(handle);
  }

  /* Forced composition = 1 of each ELIGIBLE item of the box (SUB-115). Derived
     from the box itself so the items always match what the checkout validates. */
  function revalEligibleItems(S) {
    var eligible = (S.items || []).map(function (it) { return revalNumericId(it.numericVariantId || it.variantId); }).filter(Boolean);
    var fixed = S.sizeMode === 'fixed' && S.sizes && S.sizes[0] ? Number(S.sizes[0].itemCount) : null;
    if (eligible.length && (fixed == null || fixed === eligible.length)) {
      return eligible.map(function (id) { return id + ':1'; }).join(',');
    }
    return '';
  }

  function revalParseItems(raw) {
    return String(raw || '').split(',').map(function (p) {
      var kv = p.split(':');
      return { id: revalNumericId(kv[0]), qty: Math.max(1, parseInt(kv[1], 10) || 1) };
    }).filter(function (it) { return it.id; });
  }

  /* Same arithmetic as priceDynamicBox / reval-combo-subscribe.js dynamicTotal:
     Σ unit prices (already discounted per cadence) − size or tier discount; the
     first order applies the intro % per line off the LIST price, never above the
     recurring unit. Major units in, major units out. */
  function revalDynamicTotal(S, group, items, introPercent) {
    var sub = 0, count = 0, lines = [], listBy = {};
    (S.items || []).forEach(function (it) { listBy[it.variantId] = Number(it.listPrice) || 0; });
    for (var i = 0; i < items.length; i++) {
      var gid = 'gid://shopify/ProductVariant/' + items[i].id;
      var unit = group.units ? group.units[gid] : null;
      if (unit == null) return null;
      sub += Number(unit) * items[i].qty;
      count += items[i].qty;
      lines.push({ unit: Number(unit), qty: items[i].qty, list: listBy[gid] || 0 });
    }
    if (sub <= 0) return null;
    var off = 0;
    var size = (S.sizes || []).filter(function (s) { return s.id === S.initialSizeId; })[0] || (S.sizes || [])[0];
    if (S.sizeMode !== 'range' && size && size.discountType && size.discountValue != null) {
      off = size.discountType === 'percent' ? sub * Number(size.discountValue) / 100 : Math.min(Number(size.discountValue), sub);
    } else if (S.tiers && S.tiers.length) {
      var best = null;
      S.tiers.forEach(function (t) {
        var reached = t.basis === 'amount' ? sub >= Number(t.threshold) : count >= Number(t.threshold);
        if (reached && (!best || Number(t.threshold) > Number(best.threshold))) best = t;
      });
      if (best) off = best.discountType === 'percent' ? sub * Number(best.value) / 100 : Math.min(Number(best.value), sub);
    }
    var recurring = Math.max(0, sub - off);
    var pct = Math.min(100, Math.max(0, Number(introPercent) || 0));
    var today = recurring;
    if (pct > 0 && sub > 0) {
      var keep = (100 - pct) / 100, ratio = recurring / sub;
      today = 0;
      lines.forEach(function (l) {
        var regular = l.unit * ratio;
        var base = l.list > 0 ? l.list : regular;
        today += Math.min(Math.round(base * keep * 100) / 100, regular) * l.qty;
      });
      today = Math.min(today, recurring);
    }
    return { recurring: recurring, today: today };
  }

  /* Normalised plan: everything in CENTS so it lines up with Shopify prices.
     { recurrence, label, cadence, customLabel, recurring, today, minCycles, maxCycles } */
  function revalPlansFor(wrapper) {
    var d = wrapper.dataset;
    var oneTime = Number(d.revalOneTime) || 0;

    if (d.revalKind === 'combo' && d.revalBox) {
      return revalFetchBuilder(d.revalBox).then(function (S) {
        var derived = revalEligibleItems(S);
        if (derived) wrapper.dataset.revalItems = derived;
        var items = revalParseItems(wrapper.dataset.revalItems);
        return (S.groups || []).map(function (g) {
          var gi = g.intro == null || g.intro === '' ? 0 : Number(g.intro);
          var total = revalDynamicTotal(S, g, items, isFinite(gi) && gi >= 0 ? gi : 0);
          if (!total) return null;
          return {
            recurrence: g.recurrence, label: g.freqLabel || g.recurrence, cadence: g.freqLabel || '', customLabel: false,
            recurring: Math.round(total.recurring * 100), today: Math.round(total.today * 100),
            minCycles: null, maxCycles: null
          };
        }).filter(Boolean);
      });
    }

    if (d.revalKind === 'single' && d.revalVariant) {
      return revalFetchFeed(revalNumericId(d.revalVariant)).then(function (feed) {
        return (feed && feed.plans || []).map(function (p) {
          var recurring = p.priceType === 'percent' && p.discountPercent != null && oneTime > 0
            ? Math.round(oneTime * (1 - Number(p.discountPercent) / 100))
            : Math.round((Number(p.amount) || 0) * 100);
          var intro = Number(p.firstOrderDiscountPercent) || 0;
          var today = intro > 0 && oneTime > 0 ? Math.min(Math.round(oneTime * (1 - intro / 100)), recurring) : recurring;
          return {
            recurrence: p.recurrence, label: p.label || p.cadence || p.recurrence, cadence: p.cadence || '', customLabel: !!p.customLabel,
            recurring: recurring, today: today, minCycles: p.minCycles, maxCycles: p.maxCycles
          };
        });
      });
    }

    return Promise.resolve([]);
  }

  function revalPickPlan(plans) {
    if (!plans.length) return null;
    var hit = plans.filter(function (p) { return p.recurrence === reval.recurrence; })[0];
    return hit || plans[0];
  }

  /* Writes the card from Reval's answer: price today, struck one-time, % badge,
     cadence options, renewal and commitment lines; then reveals it (or keeps a
     single product's card hidden when Reval has no plan for it). */
  function revalRender(wrapper, plans) {
    var sub = wrapper.querySelector('[data-reval-sub]');
    var heading = wrapper.querySelector('[data-reval-heading]');
    var subLabel = wrapper.querySelector('[data-ena-sub-price]');
    var oneTime = Number(wrapper.dataset.revalOneTime) || 0;
    var plan = revalPickPlan(plans);
    wrapper.removeAttribute('data-reval-pending');

    if (!sub || !subLabel) return;

    if (!plan) {
      wrapper.dataset.revalAvailable = 'false';
      sub.hidden = true;
      if (heading) heading.hidden = true;
      /* R8 · 07/10-6: one option is no choice — the block goes with it */
      wrapper.hidden = true;
      var one = wrapper.querySelector('input[name="ena-purchase-mode"][value="onetime"]');
      if (one && !one.checked) revalSelectMode(wrapper, one);
      return;
    }

    wrapper.dataset.revalAvailable = 'true';
    wrapper.hidden = false; // R8 · 07/10-6: Reval sells it, show the choice
    wrapper.dataset.revalRecurrence = plan.recurrence;
    reval.recurrence = plan.recurrence;

    var todayText = formatMoney(plan.today);
    var pct = oneTime > 0 && plan.today < oneTime ? Math.round((1 - plan.today / oneTime) * 100) : 0;
    subLabel.dataset.enaSubPrice = todayText;
    subLabel.dataset.enaSubPct = pct > 0 ? String(pct) : '';

    var priceEl = wrapper.querySelector('[data-reval-sub-price]');
    if (priceEl) priceEl.textContent = todayText;
    var wasEl = wrapper.querySelector('[data-reval-was]');
    if (wasEl) { wasEl.textContent = formatMoney(oneTime); wasEl.hidden = !(oneTime > 0 && plan.today < oneTime); }
    var tag = wrapper.querySelector('[data-reval-pct]');
    if (tag) {
      /* the intro rate leads when there is one, and says so, like Reval's buy-box */
      var introPct = plan.today < plan.recurring && oneTime > 0 ? pct : 0;
      tag.textContent = introPct > 0 ? introPct + '% OFF tu 1ª entrega' : pct + '% OFF';
      tag.hidden = pct <= 0;
    }

    var select = wrapper.querySelector('[data-reval-frequency]');
    if (select) {
      select.innerHTML = '';
      plans.forEach(function (p) {
        var opt = document.createElement('option');
        opt.value = p.recurrence;
        opt.textContent = p.label + ' · ' + formatMoney(p.today);
        opt.selected = p.recurrence === plan.recurrence;
        select.appendChild(opt);
      });
      select.disabled = plans.length < 2;
      select.classList.toggle('ena-select--single', plans.length < 2);
    }
    var cadence = wrapper.querySelector('[data-reval-cadence]');
    if (cadence) {
      /* a custom plan name hides how often it charges, so say it (SUB-57 rule) */
      cadence.textContent = plan.customLabel && plan.cadence ? 'Entrega: ' + plan.cadence.toLowerCase() : '';
      cadence.hidden = !cadence.textContent;
    }

    var renewal = wrapper.querySelector('[data-reval-renewal]');
    if (renewal) {
      renewal.textContent = plan.today < plan.recurring ? 'Después ' + formatMoney(plan.recurring) + ' por entrega' : '';
      renewal.hidden = !renewal.textContent;
    }
    var cycles = wrapper.querySelector('[data-reval-cycles]');
    if (cycles) {
      var txt = '';
      if (plan.minCycles != null && plan.maxCycles != null) txt = 'Compromiso de ' + plan.minCycles + ' a ' + plan.maxCycles + ' entregas';
      else if (plan.minCycles != null) txt = 'Compromiso mínimo de ' + plan.minCycles + ' entregas';
      else if (plan.maxCycles != null) txt = 'Suscripción por un máximo de ' + plan.maxCycles + ' entregas';
      cycles.textContent = txt;
      cycles.hidden = !txt;
    }
    revalSyncQtyNote(wrapper);

    sub.hidden = false;
    if (heading) heading.hidden = false;

    /* The design preselects subscribe & save. Honour an explicit choice made on
       this page (kept across Impact's re-renders), otherwise select it now. */
    var want = reval.mode || 'subscribe';
    var radio = wrapper.querySelector('input[name="ena-purchase-mode"][value="' + want + '"]');
    if (radio && !radio.checked) revalSelectMode(wrapper, radio);
    else applyModeToCtas(wrapper);
  }

  /* A box is one combo per delivery: the quantity selector only applies to the
     one-time purchase, and the card says so when it is above 1. */
  function revalSyncQtyNote(wrapper) {
    var note = wrapper.querySelector('[data-reval-qty-note]');
    if (!note) return;
    var qty = findQuantityInput(wrapper);
    var n = qty ? parseInt(qty.value, 10) || 1 : 1;
    note.hidden = !(wrapper.dataset.revalKind === 'combo' && wrapper.dataset.revalAvailable === 'true' && n > 1);
  }

  /* Programmatic selection (Reval's preselect / fallback to one-time) must not
     count as the shopper's choice, or a variant without a plan would pin the
     page to one-time for good. */
  function revalSelectMode(wrapper, input) {
    reval.silent = true;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    reval.silent = false;
  }

  function revalIsSubscribe(wrapper) {
    var checked = wrapper.querySelector('input[name="ena-purchase-mode"]:checked');
    return !!(checked && checked.value === 'subscribe' && wrapper.dataset.revalAvailable === 'true');
  }

  function revalCheckoutUrl(wrapper) {
    var d = wrapper.dataset;
    var qs = new URLSearchParams();
    var recurrence = d.revalRecurrence || reval.recurrence;
    if (!recurrence) return null;
    if (d.revalKind === 'combo') {
      if (!d.revalBox || !d.revalItems) return null;
      qs.set('box', d.revalBox);
      qs.set('recurrence', recurrence);
      qs.set('items', d.revalItems);
    } else if (d.revalKind === 'single') {
      var vid = revalNumericId(d.revalVariant);
      if (!vid) return null;
      var qty = findQuantityInput(wrapper);
      qs.set('variant_id', vid);
      qs.set('recurrence', recurrence);
      qs.set('quantity', String(Math.max(1, qty ? parseInt(qty.value, 10) || 1 : 1)));
    } else {
      return null;
    }
    /* qs.set() encodes once; the data attributes carry the raw values */
    if (d.revalAccent) qs.set('accent', d.revalAccent);
    if (d.revalLogo) qs.set('logo', d.revalLogo);
    if (d.revalFavicon) qs.set('favicon', d.revalFavicon);
    return '/apps/subscriptions/checkout?' + qs.toString();
  }

  /* Subscribe mode + Reval available: the main CTA (and the sticky bar, which
     clicks it) leaves for Reval's checkout. Capture phase, so Impact's own
     form handling never sees the click. The combo's items are re-derived from
     the box right before leaving (SUB-121), with a 6 s cap. */
  function revalGoToCheckout(wrapper, cta) {
    if (reval.navigating) return;
    reval.navigating = true;
    var label = firstTextNode(cta);
    var original = label ? label.nodeValue : null;
    cta.disabled = true;
    if (label) label.nodeValue = 'Redirigiendo...';
    var ready = Promise.resolve();
    if (wrapper.dataset.revalKind === 'combo' && wrapper.dataset.revalBox) {
      ready = Promise.race([
        revalFetchBuilder(wrapper.dataset.revalBox).then(function (S) {
          var items = revalEligibleItems(S);
          if (items) wrapper.dataset.revalItems = items;
        }),
        new Promise(function (resolve) { setTimeout(resolve, 6000); })
      ]).catch(function () {});
    }
    ready.then(function () {
      var url = revalCheckoutUrl(wrapper);
      if (url) { window.location.href = url; return; }
      reval.navigating = false;
      cta.disabled = false;
      if (label && original != null) label.nodeValue = original;
    });
  }

  document.addEventListener('click', function (event) {
    var cta = event.target.closest ? event.target.closest(MAIN_CTA) : null;
    if (!cta) return;
    var wrapper = document.querySelector('.ena-mode');
    if (!wrapper || !revalIsSubscribe(wrapper)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    revalGoToCheckout(wrapper, cta);
  }, true);

  /* Back from the checkout, the BFCache restores the page with the CTA frozen
     in "Redirigiendo..." — re-render from the current state. */
  window.addEventListener('pageshow', function () {
    reval.navigating = false;
    document.querySelectorAll('.ena-mode').forEach(function (wrapper) {
      document.querySelectorAll(MAIN_CTA).forEach(function (btn) { btn.disabled = false; });
      applyModeToCtas(wrapper);
    });
  });

  function initReval(wrapper) {
    var kind = wrapper.dataset.revalKind;
    if (!kind || kind === 'none' || wrapper.dataset.enaHasPlans === 'true') {
      if (kind === 'none') revalRender(wrapper, []);
      return;
    }
    if (!wrapper.hasAttribute('data-reval-pending')) return; // already resolved for this render

    if (wrapper.dataset.enaRevalReady !== '1') {
      wrapper.dataset.enaRevalReady = '1';
      wrapper.addEventListener('change', function (event) {
        var select = event.target.closest('[data-reval-frequency]');
        if (!select) return;
        reval.recurrence = select.value;
        revalPlansFor(wrapper).then(function (plans) { revalRender(wrapper, plans); }).catch(function () {});
      });
      var qty = findQuantityInput(wrapper);
      if (qty) ['change', 'input'].forEach(function (t) { qty.addEventListener(t, function () { revalSyncQtyNote(wrapper); }); });
    }

    var key = kind + ':' + (wrapper.dataset.revalBox || wrapper.dataset.revalVariant);
    wrapper.dataset.revalKey = key;
    revalPlansFor(wrapper).then(function (plans) {
      if (!document.contains(wrapper) || wrapper.dataset.revalKey !== key) return; // variant changed meanwhile
      revalRender(wrapper, plans);
    }).catch(function (err) {
      console.warn('[ena-reval] no se pudo consultar Reval:', err);
      if (!document.contains(wrapper)) return;
      /* No answer: a single product cannot show a card it cannot price; a combo
         keeps the card (Reval sells it) but without a number — the checkout
         shows the real price. */
      if (kind === 'single') revalRender(wrapper, []);
      else {
        wrapper.removeAttribute('data-reval-pending');
        wrapper.dataset.revalAvailable = 'true';
        wrapper.hidden = false; // R8 · 07/10-6
        wrapper.dataset.revalRecurrence = wrapper.dataset.revalRecurrence || '1:months';
        var skel = wrapper.querySelector('[data-reval-sub-price] .ena-skel');
        if (skel) skel.remove();
        applyModeToCtas(wrapper);
      }
    });
  }

  /* ---------------------------------------------------------------------
     Reviews jump — QA ronda 6 · Luchi. The Judge.me preview badge under the
     title ("12 reseñas") is underlined by CSS and, on click, scrolls to the
     reviews widget lower on the page. Judge.me's own handler may do the same
     when its script has booted; this one covers the case where it has not.
     --------------------------------------------------------------------- */

  function initReviewsJump(root) {
    root.querySelectorAll('.jdgm-prev-badge').forEach(function (badge) {
      if (badge.dataset.enaJumpReady === '1') return;
      badge.dataset.enaJumpReady = '1';
      badge.addEventListener('click', function (event) {
        var target =
          document.querySelector('.jdgm-rev-widg') ||
          document.querySelector('.shopify-section--apps .jdgm-widget') ||
          document.querySelector('[id$="__resenas"]');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Video feedbacks — QA ronda 6 (15/09 · item 3, US-11). Each circle opens
     its video in the block's <dialog>; "Ver todos" unhides the extra
     circles. The src is set on open and cleared on close so no video keeps
     buffering (or playing audio) behind a closed modal.
     --------------------------------------------------------------------- */

  function initVideoFeedbacks(wrapper) {
    if (wrapper.dataset.enaVfbReady === '1') return;
    wrapper.dataset.enaVfbReady = '1';

    var modal = wrapper.querySelector('[data-ena-vfb-modal]');
    var player = modal && modal.querySelector('video');
    if (!modal || !player || typeof modal.showModal !== 'function') return;

    function close() {
      player.pause();
      player.removeAttribute('src');
      player.load();
      if (modal.open) modal.close();
    }

    wrapper.querySelectorAll('[data-ena-vfb-video]').forEach(function (button) {
      button.addEventListener('click', function () {
        player.src = button.dataset.enaVfbVideo;
        modal.showModal();
        player.play().catch(function () {});
      });
    });

    var closeButton = modal.querySelector('[data-ena-vfb-close]');
    if (closeButton) closeButton.addEventListener('click', close);

    // Native <dialog> closes on Escape by itself; this catches it to stop
    // the audio. A click on the backdrop (the dialog element itself, never
    // its children) closes too.
    modal.addEventListener('close', function () {
      player.pause();
      player.removeAttribute('src');
      player.load();
    });
    modal.addEventListener('click', function (event) {
      if (event.target === modal) close();
    });

    var showAll = wrapper.querySelector('[data-ena-vfb-all]');
    if (showAll) {
      showAll.addEventListener('click', function () {
        wrapper.classList.add('ena-vfb--expanded');
        showAll.hidden = true;
      });
    }
  }

  /* ---------------------------------------------------------------------
     Tooltip — the ENA Club "?" affordance.
     --------------------------------------------------------------------- */

  function initTooltips(root) {
    root.querySelectorAll('[data-ena-tooltip]').forEach(function (el) {
      if (el.dataset.enaTipReady === '1') return;
      el.dataset.enaTipReady = '1';
      el.setAttribute('title', el.dataset.enaTooltip);
    });
  }

  /* ---------------------------------------------------------------------
     Horizontal scrollers — the athlete carousel's prev/next controls.
     Steps by one card so the arrows track the cards, not a fixed pixel
     amount, and disables itself at each end.
     --------------------------------------------------------------------- */

  function initScroller(section) {
    if (section.dataset.enaScrollerReady === '1') return;
    section.dataset.enaScrollerReady = '1';

    var track = section.querySelector('[data-ena-scroller]');
    if (!track) return;

    function step() {
      var card = track.firstElementChild;
      if (!card) return track.clientWidth * 0.8;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    section.querySelectorAll('[data-ena-scroll]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dir = btn.dataset.enaScroll === 'prev' ? -1 : 1;
        track.scrollBy({ left: dir * step(), behavior: 'smooth' });
      });
    });

    function syncEnds() {
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      section.querySelectorAll('[data-ena-scroll="prev"]').forEach(function (b) {
        b.disabled = atStart;
      });
      section.querySelectorAll('[data-ena-scroll="next"]').forEach(function (b) {
        b.disabled = atEnd;
      });
    }

    track.addEventListener('scroll', syncEnds, { passive: true });
    syncEnds();
  }


  /* ---------------------------------------------------------------------
     Upsell variant selects (QA US-04 AC1). Each complementary card carries a
     select per product option; picking one resolves the matching variant and
     writes it into the form's id field, so the cart gets what was chosen.
     --------------------------------------------------------------------- */

  function initUpsellSelectors(root) {
    if (root.dataset.enaUpsell === '1') return;
    root.dataset.enaUpsell = '1';

    /* QA ronda 4 · item 6: the selects moved out of the <form> into the info
       column, so the form is addressed by id instead of by ancestry. */
    var form = root.dataset.enaUpsellForm
      ? document.getElementById(root.dataset.enaUpsellForm)
      : root.closest('form');
    if (!form) return;

    var hidden = form.querySelector('[data-ena-upsell-variant]');
    var button = form.querySelector('button[type="submit"], [type="submit"]');
    var dataEl = root.querySelector('[data-ena-upsell-variants]');
    if (!hidden || !dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var selects = Array.prototype.slice.call(root.querySelectorAll('select[data-ena-upsell-option]'));
    if (!selects.length) return;

    function sync() {
      var chosen = selects.map(function (sel) { return sel.value; });
      var match = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var same = true;
        for (var j = 0; j < chosen.length; j++) {
          if (String(v.options[j]) !== String(chosen[j])) { same = false; break; }
        }
        if (same) { match = v; break; }
      }

      if (match) {
        hidden.value = match.id;
        if (button) {
          button.disabled = !match.available;
          button.setAttribute('aria-disabled', String(!match.available));
        }
      } else if (button) {
        /* No such combination exists - block the add rather than silently
           sending the previous variant. */
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
    }

    selects.forEach(function (sel) { sel.addEventListener('change', sync); });
    sync();
  }

  /* ---------------------------------------------------------------------
     Gallery thumbnail column — only advertise "there is more" when the
     column can actually scroll. The product's media are alt-tagged per
     variant, so the visible count changes with the selection.
     --------------------------------------------------------------------- */

  function initThumbHint(list) {
    var wrapper = list.parentElement;
    if (!wrapper) return;

    /* QA US-13 AC1: the design anchors a real 32px circular button at the foot
       of the column, not just a fade. It was a decorative ::after before, so
       there was nothing to click or tab to. */
    var btn = wrapper.querySelector('.ena-thumb-scroll');
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ena-thumb-scroll';
      btn.setAttribute('aria-label', 'Ver más imágenes');
      btn.innerHTML =
        '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
        '<path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      btn.addEventListener('click', function () {
        var step = list.clientHeight * 0.8;
        var atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
        list.scrollBy({ top: atEnd ? -list.scrollTop : step, behavior: 'smooth' });
      });
      wrapper.appendChild(btn);
    }

    function sync() {
      var scrollable = list.scrollHeight - list.clientHeight > 8;
      wrapper.classList.toggle('has-more', scrollable);
      btn.hidden = !scrollable;
      /* QA US-13 AC14: the design presents this as an indicator that more
         thumbnails exist below, so that stays the label. Only once the column
         is genuinely scrollable AND scrolled to the end does it offer to go
         back — before, a non-scrollable list counted as "at the end" and the
         button advertised "Volver al inicio" with nothing to return from. */
      var atEnd = scrollable && list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
      btn.classList.toggle('is-at-end', atEnd);
      btn.setAttribute('aria-label', atEnd ? 'Volver al inicio' : 'Ver más imágenes');
    }

    if (list.dataset.enaThumbHint !== '1') {
      list.dataset.enaThumbHint = '1';
      list.addEventListener('scroll', sync, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(sync).observe(list);
    }
    sync();
  }


  /* ---------------------------------------------------------------------
     Gallery next/prev arrows (§1). The design floats a circular arrow over
     the right edge of the main image. Impact's desktop thumbnails layout
     ships dots but no arrows, and the main media is a horizontal scroll
     container, so the control is added here and scrolls it by one slide.

     Injected rather than templated: it is purely presentational, and the
     carousel is already swipeable and keyboard-reachable without it.
     --------------------------------------------------------------------- */

  function initGalleryArrows(gallery) {
    if (gallery.dataset.enaArrowsReady === '1') return;
    var track = gallery.querySelector('media-carousel');
    var stage = gallery.querySelector('.product-gallery__ar-wrapper') || gallery;
    if (!track) return;
    gallery.dataset.enaArrowsReady = '1';

    function make(dir, label, path) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ena-gal-arrow ena-gal-arrow--' + dir;
      b.setAttribute('aria-label', label);
      // QA US-02. The design's glyph is lucide `arrow-right` (shaft + head), not
      // a chevron, and its ink spans the full 20px box. Lucide draws on a 24
      // viewBox where the arrow only occupies units 5-19, so at 20px it would
      // render ~12px of ink -- 40% short. Cropping the viewBox to the arrow's
      // own bounds puts the ink at 19.6px, matching the design's 19.
      b.innerHTML =
        '<svg viewBox="4 4 16 16" width="20" height="20" fill="none" aria-hidden="true">' +
        '<path d="' + path + '" stroke="currentColor" stroke-width="1.3" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      b.addEventListener('click', function () {
        var first = track.firstElementChild;
        var step = first ? first.getBoundingClientRect().width : track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        track.scrollBy({ left: (dir === 'next' ? 1 : -1) * (step + gap), behavior: 'smooth' });
      });
      return b;
    }

    var prev = make('prev', 'Imagen anterior', 'M19 12H5M12 19l-7-7 7-7');
    var next = make('next', 'Imagen siguiente', 'M5 12h14M12 5l7 7-7 7');
    stage.appendChild(prev);
    stage.appendChild(next);

    function sync() {
      var atStart = track.scrollLeft <= 2;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      prev.hidden = atStart;
      next.hidden = atEnd;
    }

    track.addEventListener('scroll', sync, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(sync).observe(track);
    sync();
  }

  /* ---------------------------------------------------------------------
     QA ronda 4 · item 2. Impact's media-carousel advances on a click over the
     right half of the image and goes back on the left half — the affordance
     for that was the <custom-cursor> the gallery no longer renders. Without
     the cursor those are invisible click zones, so the click is stopped before
     it reaches the carousel. Buttons and links inside (zoom, badges) still
     work, and the arrows and thumbnails remain the way to navigate.
     --------------------------------------------------------------------- */

  function initGalleryClickGuard(gallery) {
    var wrap = gallery.querySelector('.product-gallery__media-list-wrapper');
    if (!wrap || wrap.dataset.enaClickGuard === '1') return;
    wrap.dataset.enaClickGuard = '1';
    wrap.addEventListener(
      'click',
      function (event) {
        if (event.target.closest('button, a[href]')) return;
        event.stopPropagation();
      },
      true
    );
  }

  /* ---------------------------------------------------------------------
     Variant availability, both ways round.

     Shopify resolves `option_value.available` against the options *before* it,
     so on this product — Tamaño is option 1, Sabor is option 2 — Sabor dims
     correctly against the chosen size, but Tamaño never dims: with no
     preceding option, every size that exists anywhere reads as available. So
     150 g stayed lit while Fruit Punch was selected, a combination the store
     does not carry, and the shopper could land on a dead end whose only signal
     was the add-to-cart button going to "No disponible".

     This recomputes availability for every option against the current
     selection of all the *other* options, which is symmetric, and re-runs
     after each of Impact's re-renders via boot().

     Nothing is disabled. The matrix is sparse — Neutro comes in all four
     sizes, the other two flavours only in 300 g — so disabling what is
     unavailable would strand a shopper on Fruit Punch with 300 g as the only
     size they could pick. Instead a dimmed value stays clickable and carries
     the rest of the selection with it: clicking Fruit Punch from 150 g lands
     on 300 g / Fruit Punch. The dimming says "not with what you have picked",
     and acting on it always leads somewhere real.
     --------------------------------------------------------------------- */

  function readVariantMatrix(picker) {
    var tag = picker.querySelector('script[data-ena-vp-variants]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      return null;
    }
  }

  function pickerInputs(picker) {
    return Array.prototype.slice.call(
      picker.querySelectorAll('input[data-option-position][data-value-name]')
    );
  }

  /* The value chosen at each option position, indexed from 0. */
  function currentSelection(picker) {
    var sel = [];
    pickerInputs(picker).forEach(function (input) {
      if (!input.checked) return;
      sel[parseInt(input.getAttribute('data-option-position'), 10) - 1] =
        input.getAttribute('data-value-name');
    });
    return sel;
  }

  /* Does any purchasable variant carry `value` at `index`, while agreeing with
     `sel` everywhere else? `sel` holes are treated as "anything". */
  function combinationExists(variants, index, value, sel) {
    return variants.some(function (v) {
      if (!v.available) return false;
      if (v.options[index] !== value) return false;
      for (var i = 0; i < sel.length; i++) {
        if (i === index) continue;
        if (sel[i] != null && v.options[i] !== sel[i]) return false;
      }
      return true;
    });
  }

  function initVariantAvailability(picker) {
    var variants = readVariantMatrix(picker);
    if (!variants || !variants.length) return;
    var sel = currentSelection(picker);

    pickerInputs(picker).forEach(function (input) {
      var index = parseInt(input.getAttribute('data-option-position'), 10) - 1;
      var value = input.getAttribute('data-value-name');
      var label = input.closest('.ena-vp__swatch, .ena-vp__row');
      if (!label) return;
      var ok = combinationExists(variants, index, value, sel);
      label.classList.toggle('is-unavailable', !ok);
      /* The selected value is never dimmed, even when the combination it sits
         in has since become unavailable — dimming what the shopper is looking
         at reads as an error rather than as guidance. */
      if (input.checked) label.classList.remove('is-unavailable');
    });
  }

  /* Capture phase, so the other groups are corrected before Impact's own
     delegated change handler on document.body reads the checked inputs and
     fetches a combination. */
  function onVariantOptionChange(event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('input[data-option-position][data-value-name]')) return;
    var picker = input.closest('variant-picker');
    if (!picker) return;
    var variants = readVariantMatrix(picker);
    if (!variants || !variants.length) return;

    var index = parseInt(input.getAttribute('data-option-position'), 10) - 1;
    var value = input.getAttribute('data-value-name');
    var sel = currentSelection(picker);

    if (combinationExists(variants, index, value, sel)) return;

    /* Carry as much of the current selection as possible: score the candidates
       by how many of the other options they already agree with, so switching
       flavour moves the size only when it has to. */
    var best = null;
    var bestScore = -1;
    variants.forEach(function (v) {
      if (!v.available || v.options[index] !== value) return;
      var score = 0;
      for (var i = 0; i < sel.length; i++) {
        if (i !== index && sel[i] != null && v.options[i] === sel[i]) score++;
      }
      if (score > bestScore) { bestScore = score; best = v; }
    });
    if (!best) return;

    pickerInputs(picker).forEach(function (other) {
      var i = parseInt(other.getAttribute('data-option-position'), 10) - 1;
      if (i === index) return;
      if (other.getAttribute('data-value-name') === best.options[i]) other.checked = true;
    });
  }

  document.addEventListener('change', onVariantOptionChange, true);

  /* ---------------------------------------------------------------------
     QA-13. The sticky bar must be visible whenever the main add-to-cart is out
     of view - including at first paint, which is where QA caught it hidden.
     Impact gates it on its own "scope" observer instead, which only fires once
     a marker element has been scrolled past.

     Observing the CTA itself is both simpler and what the criterion actually
     says. The bar's visibility is driven only by `ena-sticky-on` (see the CSS),
     so Impact's own toggling cannot fight this.
     --------------------------------------------------------------------- */

  var MAIN_CTA = '.ena-cart-row buy-buttons button[type="submit"]:not(.shopify-payment-button__button)';
  var gateIO = null;
  var gateCta = null;

  function applyStickyState(visible) {
    var bar = document.querySelector('product-quick-add');
    if (bar) bar.classList.toggle('ena-sticky-on', !visible);
    /* QA ronda 4 · items 13/14: the ported ENA bar follows the same gate. */
    var satc = document.querySelector('[data-ena-satc]');
    if (satc) satc.classList.toggle('is-visible', !visible);
    /* QA ronda 4 · item 16 AC5: reserve the bar's height at the foot of the
       page while it is shown, so it never sits over the footer's content. */
    document.documentElement.classList.toggle('ena-sticky-visible', !visible);
  }

  function initStickyGate() {
    var cta = document.querySelector(MAIN_CTA);
    if (!cta) return;
    if (!document.querySelector('product-quick-add') && !document.querySelector('[data-ena-satc]')) return;
    /* Impact replaces the CTA node when it re-renders the buy box, which would
       leave the observer watching a detached element; re-target it then. */
    if (gateCta === cta && document.contains(cta)) return;

    if (!('IntersectionObserver' in window)) {
      applyStickyState(false);
      return;
    }

    if (!gateIO) {
      gateIO = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) applyStickyState(entries[i].isIntersecting);
        },
        /* a sliver of the button counts as "in view", so the bar does not flicker
           on and off while the CTA straddles the fold */
        { threshold: 0.01 }
      );
    }
    if (gateCta) gateIO.unobserve(gateCta);
    gateCta = cta;
    gateIO.observe(cta);
  }

  /* ---------------------------------------------------------------------
     QA ronda 4 · items 13/14/16. The ENA sticky bar, ported from the
     develop-latech theme. It owns no state: everything it shows is read from
     the buy box (variant picker, purchase-mode radios, main CTA), and every
     action it offers is forwarded there — so the two can never disagree, and
     Impact's re-renders are picked up on the next boot().
     --------------------------------------------------------------------- */

  var satcNarrow = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };

  /* R8 · 07/10-4 — "Precio tachado en el sticky add to cart". The bar showed
     only what you pay ($30.000) while the price block above strikes the list
     price ($40.000). The list price comes from Impact's own <script data-variant>
     JSON, which the ENA picker emits and Impact swaps on every variant change
     (same re-render as the buy buttons the bar mirrors), times the quantity so
     a pack of 2 strikes 2 x list. Nothing when the variant has no real discount
     (compare_at missing, equal or inverted, as Monodosis once was). */
  function stickyCompareAt() {
    var node = document.querySelector('variant-picker script[data-variant]');
    if (!node) return '';
    var v;
    try { v = JSON.parse(node.textContent); } catch (e) { return ''; }
    if (!v || !(v.compare_at_price > v.price)) return '';
    var qtyInput =
      document.querySelector('.ena-cart-row input[name="quantity"]') ||
      document.querySelector('quantity-selector input[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    return formatMoney(v.compare_at_price * qty);
  }

  /* R8 · 08/10-6 — the bar's price row follows the PDP's hierarchy (R8 ·
     08/10-4): price first, then the struck list price, then the "X% OFF"
     pill. The percentage is the variant's own (compare_at vs price), so it
     does not move with the pack quantity; the pill copy comes from the
     section setting (data-ena-satc-badge, "{pct}% OFF"). */
  function stickyDiscountPct() {
    var node = document.querySelector('variant-picker script[data-variant]');
    if (!node) return 0;
    var v;
    try { v = JSON.parse(node.textContent); } catch (e) { return 0; }
    if (!v || !(v.compare_at_price > v.price)) return 0;
    return Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
  }

  function stickyPriceRow(bar, now, was) {
    var html = '<span class="ena-satc__now">' + now + '</span>';
    if (was) {
      html += ' <s class="ena-satc__was">' + was + '</s>';
      var pct = stickyDiscountPct();
      var tpl = bar.getAttribute('data-ena-satc-badge') || '{pct}% OFF';
      if (pct > 0 && tpl) html += ' <span class="ena-satc__badge">' + tpl.replace('{pct}', pct) + '</span>';
    }
    return html;
  }


  /* R9 · 21/10-4 — the bar's thumbnail follows the selected variant (the
     flavour pouch, not always the product's first image). The variant JSON
     Impact keeps in <script data-variant> carries featured_image.src; without
     one the product image (data-ena-satc-thumb-default) comes back. Shopify's
     CDN takes the size from the width query param. */
  function refreshStickyThumb(bar) {
    var img = bar.querySelector('[data-ena-satc-thumb]');
    if (!img) return;
    var node = document.querySelector('variant-picker script[data-variant]');
    var v = null;
    if (node) { try { v = JSON.parse(node.textContent); } catch (e) { v = null; } }
    var src = v && v.featured_image && v.featured_image.src;
    if (!src && v && v.featured_media && v.featured_media.preview_image) src = v.featured_media.preview_image.src;
    if (src) {
      if (src.indexOf('//') === 0) src = 'https:' + src;
      src += (src.indexOf('?') === -1 ? '?' : '&') + 'width=120';
    } else {
      src = img.getAttribute('data-ena-satc-thumb-default') || '';
    }
    if (src && img.getAttribute('src') !== src) {
      img.removeAttribute('srcset');
      img.setAttribute('src', src);
    }
  }

  function refreshStickyAtc() {
    var bar = document.querySelector('[data-ena-satc]');
    if (!bar) return;
    refreshStickyThumb(bar);

    /* selects follow the checked picker inputs */
    bar.querySelectorAll('select[data-ena-satc-option]').forEach(function (sel) {
      var pos = sel.getAttribute('data-ena-satc-option');
      var checked = document.querySelector(
        'variant-picker input[data-option-position="' + pos + '"]:checked'
      );
      if (checked) {
        var name = checked.getAttribute('data-value-name');
        if (sel.value !== name) sel.value = name;
      }
    });

    /* swatch copies the selected Sabor dot, whatever source the picker used */
    var swatch = bar.querySelector('[data-ena-satc-swatch]');
    if (swatch) {
      var dot = document.querySelector('.ena-vp__swatch input:checked ~ .ena-vp__dot');
      swatch.setAttribute('style', dot ? dot.getAttribute('style') || '' : '');
      swatch.classList.toggle('ena-satc__swatch--image', !!(dot && dot.classList.contains('ena-vp__dot--image')));
      swatch.hidden = !dot;
    }

    /* mode toggle follows the radios; without a mode block it has no meaning */
    var modeWrap = bar.querySelector('[data-ena-satc-mode]');
    if (modeWrap) {
      var subRadio = document.querySelector('input[name="ena-purchase-mode"][value="subscribe"]');
      /* ENA-15: the card may be in the DOM but hidden (Reval has no plan). */
      var subHidden = !!(subRadio && subRadio.closest('[data-reval-sub]') && subRadio.closest('[data-reval-sub]').hidden);
      modeWrap.hidden = !subRadio || subHidden;
      var mode = subRadio && subRadio.checked ? 'subscribe' : 'onetime';
      modeWrap.querySelectorAll('button').forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-mode') === mode);
      });
      var subBtn = modeWrap.querySelector('button[data-mode="subscribe"]');
      var subLabel = document.querySelector('[data-ena-sub-pct]');
      if (subBtn && subLabel) {
        var base = subBtn.getAttribute('data-label') || subBtn.textContent.trim();
        var pct = subLabel.getAttribute('data-ena-sub-pct');
        var text = /%/.test(base) || !pct || pct === '0' ? base : base + ' -' + pct + '%';
        if (subBtn.textContent.trim() !== text) subBtn.textContent = text;
      }
    }

    /* label and price mirror the main CTA, which already reflects variant,
       quantity/pack and purchase mode */
    var mainCta = document.querySelector(MAIN_CTA);
    var add = bar.querySelector('[data-ena-satc-add]');
    var priceEl = bar.querySelector('[data-ena-satc-price]');
    if (mainCta && add) {
      var labelNode = firstTextNode(mainCta);
      var label = labelNode ? labelNode.nodeValue.trim() : add.textContent.trim();
      var priceSpan = mainCta.querySelector('.buy-buttons__price');
      var priceHTML = priceSpan
        ? priceSpan.innerHTML.replace(/^(\s|&nbsp;| )*·\s*/, '').trim()
        : '';
      /* R8 · 07/10-4: the bar strikes the list price like the price block does.
         In subscribe mode the CTA already carries its own struck one-time price.
         R8 · 08/10-6: one-time → price, struck list price, "X% OFF" pill. */
      var foldHTML = priceHTML;
      if (priceHTML && priceHTML.indexOf('buy-buttons__was') === -1) {
        var was = stickyCompareAt();
        priceHTML = stickyPriceRow(bar, priceHTML, was);
        /* on phones the row folds into the CTA label: price + struck, no pill */
        foldHTML = '<span class="ena-satc__now">' + foldHTML + '</span>' + (was ? ' <s class="ena-satc__was">' + was + '</s>' : '');
      }
      if (priceEl) priceEl.innerHTML = priceHTML;
      /* on phones the standalone price is hidden, so it folds into the label */
      var html = satcNarrow.matches && foldHTML ? label + ' · ' + foldHTML : label;
      if (add.innerHTML !== html) add.innerHTML = html;
      add.disabled = mainCta.disabled;
    }

    /* the page reserves exactly the bar's height (see CSS) */
    document.documentElement.style.setProperty('--ena-satc-h', bar.offsetHeight + 'px');
  }

  function initStickyAtc() {
    var bar = document.querySelector('[data-ena-satc]');
    if (!bar) return;

    if (bar.dataset.enaSatcReady !== '1') {
      bar.dataset.enaSatcReady = '1';

      /* bar select -> picker input. Clicking the radio runs the picker's own
         availability logic (capture handler above) and Impact's fetch. */
      bar.addEventListener('change', function (event) {
        var sel = event.target.closest('select[data-ena-satc-option]');
        if (!sel) return;
        var pos = sel.getAttribute('data-ena-satc-option');
        var input = Array.prototype.find.call(
          document.querySelectorAll('variant-picker input[data-option-position="' + pos + '"]'),
          function (i) { return i.getAttribute('data-value-name') === sel.value; }
        );
        if (input && !input.checked) input.click();
        refreshStickyAtc();
      });

      bar.addEventListener('click', function (event) {
        var toggle = event.target.closest('[data-ena-satc-mode] button');
        if (toggle) {
          var radio = document.querySelector(
            'input[name="ena-purchase-mode"][value="' + toggle.getAttribute('data-mode') + '"]'
          );
          if (radio && !radio.checked) radio.click();
          refreshStickyAtc();
          return;
        }

        var add = event.target.closest('[data-ena-satc-add]');
        if (add) {
          var mainCta = document.querySelector(MAIN_CTA);
          if (!mainCta || mainCta.disabled) return;
          /* submit the main form: same quantity, selling plan and cart drawer */
          mainCta.click();
          add.classList.add('is-added');
          setTimeout(function () { add.classList.remove('is-added'); }, 1500);
        }
      });

      if (satcNarrow.addEventListener) satcNarrow.addEventListener('change', refreshStickyAtc);
      window.addEventListener('resize', refreshStickyAtc);
    }

    refreshStickyAtc();
  }

  /* ------------------------------------------------------------------- */

  var bootCount = 0;

  function boot(root) {
    var scope = root || document;
    /* QA ronda 4 · reveal: the gallery and the buy box carry data-reveal for the
       theme's scroll-reveal engine, which only scans on load and on
       shopify:section:load. Impact re-renders both on variant change, and a
       fresh node would sit at opacity 0 forever - so after the first boot any
       unwatched reveal target inside the product is shown straight away. */
    if (bootCount > 0) {
      scope.querySelectorAll('.product [data-reveal]:not(.reveal-watched)').forEach(function (el) {
        el.classList.add('reveal-watched', 'reveal-in');
      });
    }
    bootCount++;
    scope.querySelectorAll('[data-ena-countdown]').forEach(startCountdown);
    scope.querySelectorAll('[data-ena-packs]').forEach(initPacks);
    scope.querySelectorAll('.ena-mode').forEach(function (wrapper) {
      initModes(wrapper);
      initReval(wrapper);
      /* Re-applied on every boot, not only on first bind: Impact re-renders the
         sticky bar on its own, and the fresh markup must pick the mode up. */
      applyModeToCtas(wrapper);
    });
    scope.querySelectorAll('.ena-athletes').forEach(initScroller);
    scope.querySelectorAll('.product-gallery__thumbnail-list').forEach(initThumbHint);
    scope.querySelectorAll('.product-gallery').forEach(initGalleryArrows);
    scope.querySelectorAll('.product-gallery').forEach(initGalleryClickGuard);
    scope.querySelectorAll('[data-ena-upsell]').forEach(initUpsellSelectors);
    scope.querySelectorAll('variant-picker').forEach(initVariantAvailability);
    initStickyGate();
    initStickyAtc();
    initTooltips(scope);
    initReviewsJump(scope);
    scope.querySelectorAll('[data-ena-vfb]').forEach(initVideoFeedbacks);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // Impact re-renders product-info on variant change; re-bind what reappears.
  document.addEventListener('variant:change', function () {
    boot(document);
  });

  // The countdown rewrites its own text every second, which is itself a
  // childList mutation — debounce so re-binding stays cheap.
  var rebindTimer = null;
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        clearTimeout(rebindTimer);
        rebindTimer = setTimeout(function () {
          boot(document);
        }, 200);
        return;
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();